/**
 * Wire-level Event Log: every CALL / CALLRESULT / CALLERROR that flowed
 * through CallDispatcher. Paired by messageId so a CALL and its response
 * share a row, with the response payload available via an Expand toggle.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEventLogStore, type MessageType, type UiEventLogEntry } from '@/state/useEventLogStore';
import { useStationStore } from '@/state/useStationStore';
import { useUIStore } from '@/state/useUIStore';
import { useConfirm } from '@/hooks/useConfirm';
import { CopyButton } from '@/ui/components/CopyButton';
import { Toggle } from '@/ui/components/Toggle';
import { formatTime } from '@/lib/format';

interface Pair {
  readonly call: UiEventLogEntry | null;
  readonly response: UiEventLogEntry | null;
  readonly messageId: string;
}

interface Filters {
  readonly hideHeartbeat: boolean;
  readonly hideMeterValues: boolean;
  readonly errorsOnly: boolean;
  readonly search: string;
}

export function EventLogTab(): JSX.Element {
  const entries = useEventLogStore((s) => s.entries);
  const clear = useEventLogStore((s) => s.clear);
  const stationId = useStationStore((s) => s.stationId);
  const autoScroll = useUIStore((s) => s.eventLogAutoScroll);
  const setAutoScroll = useUIStore((s) => s.setEventLogAutoScroll);
  const confirm = useConfirm();

  const [filters, setFilters] = useState<Filters>({
    hideHeartbeat: true,
    hideMeterValues: false,
    errorsOnly: false,
    search: '',
  });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const pairs = useMemo(() => pairFrames(entries, filters), [entries, filters]);

  // NIT-011: auto-scroll the list end into view whenever pairs change AND
  // the preference is ON. Smart-pause: if the user has scrolled up
  // manually (scrollHeight - scrollTop > clientHeight + 32 px), we skip
  // until they return to the bottom.
  const listRef = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!autoScroll) return;
    const el = listRef.current;
    if (!el) return;
    const scrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight > 32;
    if (!scrolledUp) {
      el.scrollTop = el.scrollHeight;
    }
  }, [pairs, autoScroll]);

  return (
    <div
      role="tabpanel"
      id="tab-panel-eventLog"
      aria-labelledby="tab-eventLog"
      className="space-y-3 p-4"
    >
      <header className="flex flex-wrap items-center gap-3 rounded border border-slate-700 bg-slate-900/60 p-3 text-xs text-slate-300">
        <span className="flex items-center gap-1.5">
          <Toggle
            checked={filters.hideHeartbeat}
            onChange={(v) => setFilters({ ...filters, hideHeartbeat: v })}
            label="Hide Heartbeat"
            testId="filter-hide-heartbeat"
          />
          Hide Heartbeat
        </span>
        <span className="flex items-center gap-1.5">
          <Toggle
            checked={filters.hideMeterValues}
            onChange={(v) => setFilters({ ...filters, hideMeterValues: v })}
            label="Hide MeterValues"
            testId="filter-hide-metervalues"
          />
          Hide MeterValues
        </span>
        <span className="flex items-center gap-1.5">
          <Toggle
            checked={filters.errorsOnly}
            onChange={(v) => setFilters({ ...filters, errorsOnly: v })}
            label="Errors only"
            testId="filter-errors-only"
          />
          Errors only
        </span>
        <span className="flex items-center gap-1.5">
          <Toggle
            checked={autoScroll}
            onChange={(v) => setAutoScroll(v)}
            label="Auto-scroll"
            testId="filter-autoscroll"
          />
          Auto-scroll
        </span>
        <label className="flex items-center gap-1">
          Search
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="action or id"
            className="ml-1 rounded border border-slate-600 bg-slate-950 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="filter-search"
          />
        </label>
        <span className="ml-auto" data-testid="event-count">
          {pairs.length} pair{pairs.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => download(pairs, entries)}
          className="rounded bg-slate-700 px-2 py-1 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid="event-download"
        >
          Download JSON
        </button>
        <button
          type="button"
          onClick={() => {
            confirm('clear the entire Event Log (in-memory and Dexie)', () => {
              void clear(stationId);
            });
          }}
          className="rounded bg-rose-700 px-2 py-1 text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
          data-testid="event-clear"
        >
          Clear
        </button>
      </header>

      <ul
        ref={listRef}
        className="max-h-[60vh] space-y-1 overflow-y-auto text-xs"
        data-testid="event-list"
      >
        {pairs.map((pair) => {
          const isExpanded = expanded.has(pair.messageId);
          const isError = pair.response?.messageType === 'CALLERROR';
          const action =
            pair.call?.action ??
            (pair.response?.messageType === 'CALLERROR'
              ? 'CALLERROR (unmatched)'
              : 'CALLRESULT (unmatched)');
          const dirArrow = pair.call?.direction === 'out' ? '↑' : '↓';
          return (
            <li
              key={pair.messageId}
              className={`rounded ${isError ? 'border border-rose-700/60' : 'border border-slate-800'} bg-slate-900/40`}
              data-testid={`event-pair-${pair.messageId}`}
            >
              <button
                type="button"
                onClick={() => {
                  const next = new Set(expanded);
                  if (next.has(pair.messageId)) next.delete(pair.messageId);
                  else next.add(pair.messageId);
                  setExpanded(next);
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-800/40 focus:outline-none focus:ring-2 focus:ring-slate-400"
                aria-expanded={isExpanded}
                data-testid={`event-toggle-${pair.messageId}`}
              >
                <span className="font-mono text-slate-500">{dirArrow}</span>
                <span className={`font-medium ${isError ? 'text-rose-300' : 'text-slate-100'}`}>
                  {action}
                </span>
                <span className="text-slate-500">
                  {pair.call ? formatTime(pair.call.at) : ''}
                </span>
                <span className="ml-auto font-mono text-slate-600">
                  {truncate(pair.messageId, 12)}
                </span>
              </button>
              {isExpanded ? (
                <div className="grid gap-2 border-t border-slate-800 p-3 text-xs sm:grid-cols-2">
                  {pair.call ? (
                    <Payload
                      label={messageTypeLabel(pair.call.messageType, pair.call.direction)}
                      payload={pair.call.payload}
                    />
                  ) : (
                    <span className="text-slate-500">no CALL captured</span>
                  )}
                  {pair.response ? (
                    <Payload
                      label={messageTypeLabel(pair.response.messageType, pair.response.direction)}
                      payload={pair.response.payload}
                    />
                  ) : (
                    <span className="text-slate-500">no response captured (yet)</span>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
        {pairs.length === 0 ? (
          <li className="rounded border border-slate-800 bg-slate-900/40 p-4 text-center text-slate-500">
            No events match the current filters.
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function Payload({ label, payload }: { label: string; payload: unknown }): JSX.Element {
  const text = JSON.stringify(payload, null, 2);
  return (
    <section>
      <div className="mb-1 flex items-center gap-2 text-slate-500">
        <span>{label}</span>
        <CopyButton value={text} label="Copy" testId="payload" />
      </div>
      <pre className="overflow-x-auto rounded bg-slate-950 p-2 text-[10px] text-slate-200">
        {text}
      </pre>
    </section>
  );
}

function messageTypeLabel(type: MessageType, dir: 'in' | 'out'): string {
  const arrow = dir === 'out' ? '↑ sent' : '↓ received';
  return `${arrow} · ${type}`;
}

function pairFrames(entries: readonly UiEventLogEntry[], filters: Filters): readonly Pair[] {
  const byMessageId = new Map<string, Pair>();
  const order: string[] = [];
  for (const e of entries) {
    let pair = byMessageId.get(e.messageId);
    if (!pair) {
      pair = { call: null, response: null, messageId: e.messageId };
      byMessageId.set(e.messageId, pair);
      order.push(e.messageId);
    }
    if (e.messageType === 'CALL') {
      byMessageId.set(e.messageId, { ...pair, call: e });
    } else {
      byMessageId.set(e.messageId, { ...pair, response: e });
    }
  }
  const out: Pair[] = [];
  for (const id of order) {
    const pair = byMessageId.get(id);
    if (!pair) continue;
    if (!includePair(pair, filters)) continue;
    out.push(pair);
  }
  return out;
}

function includePair(pair: Pair, filters: Filters): boolean {
  const action = pair.call?.action ?? '';
  if (filters.hideHeartbeat && action === 'Heartbeat') return false;
  if (filters.hideMeterValues && action === 'MeterValues') return false;
  if (filters.errorsOnly && pair.response?.messageType !== 'CALLERROR') return false;
  if (filters.search.length > 0) {
    const needle = filters.search.toLowerCase();
    const hay = `${action.toLowerCase()} ${pair.messageId.toLowerCase()}`;
    if (!hay.includes(needle)) return false;
  }
  return true;
}

function download(pairs: readonly Pair[], entries: readonly UiEventLogEntry[]): void {
  const blob = new Blob([JSON.stringify({ pairs, raw: entries }, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ocpp-events-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
